import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import AnalyseIA from './AnalyseIA';
import { lignesDuBloc, totauxDuBloc, blocsAnalysables } from '../utils/lignesDuDashboard';
import { AlertCircle, EyeOff, Users, TrendingUp } from 'lucide-react';
import GA4PerformanceChart from './GA4PerformanceChart';
import GA4ChannelsChart from './GA4ChannelsChart';
import GA4GeographyChart from './GA4GeographyChart';
import GA4DevicesChart from './GA4DevicesChart';
import GA4BrowsersChart from './GA4BrowsersChart';
import GA4LanguagesChart from './GA4LanguagesChart';
import GA4PagesChart from './GA4PagesChart';
import GA4EventsChart from './GA4EventsChart';
import GA4InsightsPanel from './GA4InsightsPanel';
import GA4EcommerceFunnel from './GA4EcommerceFunnel';
import GA4EcommerceProducts from './GA4EcommerceProducts';
import WeekdaysTable from './WeekdaysTable';
import DashboardLoadingScreen from './DashboardLoadingScreen';
import HiddenModulesModal from './HiddenModulesModal';
import { useClientModuleVisibility } from '../hooks/useClientModuleVisibility';
import { useAppliedTemplate } from '../utils/metricsUtils';
import { dateLocale, jourIso } from '../utils/periodUtils';

/**
 * Agrège les données quotidiennes selon la granularité
 * (réplique de la logique backend pour éviter les rechargements)
 */
function aggregateByGranularity(dailyData, granularity) {
  if (!dailyData || dailyData.length === 0) return [];

  if (granularity === 'day') {
    return dailyData;
  }

  // Pour les autres granularités, grouper par période
  const grouped = {};

  dailyData.forEach(day => {
    const periodKey = getPeriodKey(day.date, granularity);

    if (!grouped[periodKey]) {
      grouped[periodKey] = {
        date: periodKey,
        visits: 0,
        uniqueVisitors: 0,
        pageviews: 0,
        // Métriques de conversion
        conversions: 0,
        conversionsValue: 0,
        // Métriques e-commerce
        transactions: 0,
        totalRevenue: 0,
        bounceSum: 0,
        timeSum: 0,
        actionsSum: 0,
        dayCount: 0
      };
    }

    grouped[periodKey].visits += day.visits || 0;
    grouped[periodKey].uniqueVisitors += day.uniqueVisitors || day.activeUsers || 0;
    grouped[periodKey].pageviews += day.pageviews || 0;
    // Métriques de conversion
    grouped[periodKey].conversions += day.conversions || 0;
    grouped[periodKey].conversionsValue += day.conversionsValue || 0;
    // Métriques e-commerce
    grouped[periodKey].transactions += day.transactions || 0;
    grouped[periodKey].totalRevenue += day.totalRevenue || 0;
    grouped[periodKey].bounceSum += (day.bounceRate || 0) * (day.visits || 0);
    grouped[periodKey].timeSum += (day.avgTimeOnSite || 0) * (day.visits || 0);
    grouped[periodKey].actionsSum += (day.actionsPerVisit || 0) * (day.visits || 0);
    grouped[periodKey].dayCount++;
  });

  // Calculer les moyennes et taux
  return Object.values(grouped).map(period => ({
    date: period.date,
    visits: period.visits,
    uniqueVisitors: period.uniqueVisitors,
    pageviews: period.pageviews,
    // Métriques de conversion
    conversions: period.conversions,
    conversionsValue: period.conversionsValue,
    conversionRate: period.visits > 0 ? (period.conversions / period.visits * 100) : 0,
    // Métriques e-commerce
    transactions: period.transactions,
    totalRevenue: period.totalRevenue,
    transactionRate: period.visits > 0 ? (period.transactions / period.visits * 100) : 0,
    bounceRate: period.visits > 0
      ? (period.bounceSum / period.visits)
      : 0,
    avgTimeOnSite: period.visits > 0
      ? (period.timeSum / period.visits)
      : 0,
    actionsPerVisit: period.visits > 0
      ? (period.actionsSum / period.visits)
      : 0
  })).sort((a, b) => a.date.localeCompare(b.date));
}

/**
 * Retourne la clé de période pour une date
 */
function getPeriodKey(dateStr, granularity) {
  // Minuit local, et non minuit UTC : voir `dateLocale`. Le repère doit être le même à la
  // lecture et à l'écriture, sans quoi le lundi d'une semaine saute d'un jour.
  const date = dateLocale(dateStr);

  switch (granularity) {
    case 'week':
      // Trouver le lundi de la semaine
      const day = date.getDay();
      const diff = date.getDate() - day + (day === 0 ? -6 : 1);
      const monday = new Date(date);
      monday.setDate(diff);
      return jourIso(monday);

    case 'month':
      return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;

    case 'quarter':
      const quarter = Math.floor(date.getMonth() / 3) + 1;
      return `${date.getFullYear()}-Q${quarter}`;

    case 'year':
      return `${date.getFullYear()}`;

    default:
      return dateStr;
  }
}

/**
 * GA4DashboardView - Vue dashboard pour Google Analytics 4
 *
 * Affiche:
 * - Modal de sélection de propriété (si aucune propriété sélectionnée)
 * - Composant Résultats (GA4PerformanceChart) - style identique à Matomo/Google Ads
 */

const GA4DashboardView = ({
  /**
   * L'analyse IA est-elle ouverte à cet utilisateur ?
   *
   * Un membre d'organisation est jugé sur son plan, un accès client sur la permission
   * `analyse-ia` accordée dans Organisation › Accès clients. La règle est calculée une fois
   * dans `App` et descendue ici : `autorise` valant `true` par défaut, ne rien passer montrait
   * le bouton à un client que le serveur refuse ensuite.
   */
  analyseAutorisee = false,
  // Notes à reporter sur le graphique. Traversent la vue sans y être lues.
  dashboardNotes = [],
  token,
  selectedPeriod,
  customStartDate,
  customEndDate,
  ga4Properties,
  filteredGA4Properties = [],
  setGA4Properties,
  ga4SelectedPropertyId,
  setGA4SelectedPropertyId,
  ga4DashboardData,
  setGA4DashboardData,
  ga4Loading,
  setGA4Loading,
  ga4Granularity,
  setGA4Granularity,
  KPICard,
  formatNumber,
  userRole,
  compareEnabled = false,
  comparisonType = ''
}) => {
  const [error, setError] = useState(null);
  const [propertiesLoading, setPropertiesLoading] = useState(true);
  const [waitingForData, setWaitingForData] = useState(true); // Attendre les données avant d'afficher

  // Calculer le clientId effectif pour la visibilité des modules
  const effectiveClientId = useMemo(() => {
    return ga4SelectedPropertyId ? `ga4_${ga4SelectedPropertyId}` : null;
  }, [ga4SelectedPropertyId]);

  // Hook pour la visibilité des modules par client
  const {
    isModuleVisible: isClientModuleVisible,
    toggleModuleVisibility: toggleClientModuleVisibility,
    setModuleVisibility: setClientModuleVisibility,
    hiddenModules: clientHiddenModules
  } = useClientModuleVisibility(effectiveClientId, userRole);

  /**
   * Le parcours d'achat et les achats e-commerce ne s'affichent que sous le template E-commerce.
   *
   * Ils décrivent un tunnel de vente : sur une propriété suivie en génération de leads ou en
   * lecture de contenu, ils occupent deux sections entières pour un entonnoir qui n'existe pas.
   * Le template dit ce qu'on est venu mesurer ; c'est donc lui qui décide.
   *
   * **`null` laisse tout en place**, et c'est délibéré : il signifie qu'aucun template n'a
   * jamais été appliqué sur cette propriété, pas qu'on en a choisi un autre. Masquer ces blocs
   * dans ce cas retirerait sans prévenir deux composants à tous ceux qui n'ont jamais ouvert
   * le sélecteur.
   */
  const templateAnalytics = useAppliedTemplate(ga4SelectedPropertyId || 'global', 'ga4');
  const blocsEcommerceVisibles = !templateAnalytics || templateAnalytics === 'ecommerce';

  // State pour le modal des modules masqués
  const [isHiddenModulesModalOpen, setIsHiddenModulesModalOpen] = useState(false);

  // State pour les données navigateurs (chargées séparément)
  const [browsersData, setBrowsersData] = useState(null);
  const [browsersLoading, setBrowsersLoading] = useState(false);

  // State pour les données langues (chargées séparément)
  const [languagesData, setLanguagesData] = useState(null);
  const [languagesLoading, setLanguagesLoading] = useState(false);

  // Ref pour suivre si on a chargé des données fraîches dans cette session
  const hasFetchedFreshDataRef = useRef(false);
  // Une réponse lente d'une période abandonnée ne doit pas écraser la période courante
  const dashboardAbortRef = useRef(null);
  const browsersAbortRef = useRef(null);
  const languagesAbortRef = useRef(null);

  // Charger la liste des propriétés GA4 au montage
  useEffect(() => {
    const fetchGA4Properties = async () => {
      if (!token) return;

      setPropertiesLoading(true);
      setWaitingForData(true); // Reset au montage
      try {
        const response = await fetch('/api/dashboard/ga4/properties', {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });
        const data = await response.json();

        if (data.success && data.properties) {
          setGA4Properties(data.properties);
        } else {
          setError(data.error || 'Erreur lors du chargement des propriétés Google Analytics');
          setWaitingForData(false);
        }
      } catch (err) {
        console.error('Erreur fetch propriétés GA4:', err);
        setError('Impossible de charger les propriétés Google Analytics');
        setWaitingForData(false);
      } finally {
        setPropertiesLoading(false);
      }
    };

    fetchGA4Properties();
  }, [token, setGA4Properties]);

  // Auto-sélectionner si une seule propriété GA4 filtrée disponible
  useEffect(() => {
    if (!propertiesLoading && filteredGA4Properties.length === 1) {
      const currentPropertyValid = ga4SelectedPropertyId && filteredGA4Properties.find(p => p.id === ga4SelectedPropertyId);
      if (!currentPropertyValid) {
        console.log('🎯 [GA4] Auto-sélection de la seule propriété:', filteredGA4Properties[0].name);
        setGA4SelectedPropertyId(filteredGA4Properties[0].id);
      }
    }
    // Si plusieurs propriétés ou aucune, on arrête d'attendre (afficher le sélecteur ou message)
    if (!propertiesLoading && filteredGA4Properties.length !== 1) {
      setWaitingForData(false);
    }
  }, [propertiesLoading, filteredGA4Properties, ga4SelectedPropertyId, setGA4SelectedPropertyId]);

  // Arrêter d'attendre quand les données du dashboard sont chargées (après le chargement initial)
  useEffect(() => {
    // Ne pas déclencher si les propriétés ne sont pas encore chargées
    // ET s'assurer qu'on a bien chargé des données fraîches (pas des données en cache)
    if (!propertiesLoading && ga4SelectedPropertyId && ga4DashboardData && !ga4Loading && hasFetchedFreshDataRef.current) {
      setWaitingForData(false);
    }
  }, [propertiesLoading, ga4SelectedPropertyId, ga4DashboardData, ga4Loading]);

  // Charger les données du dashboard quand la propriété ou la période change
  // Note: On récupère toujours les données quotidiennes, l'agrégation se fait côté client
  const fetchDashboardData = useCallback(async () => {
    if (!token || !ga4SelectedPropertyId) return;

    dashboardAbortRef.current?.abort();
    const controller = new AbortController();
    dashboardAbortRef.current = controller;

    setGA4Loading(true);
    setError(null);

    try {
      const response = await fetch('/api/dashboard/ga4/timeline', {
        signal: controller.signal,
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          propertyId: ga4SelectedPropertyId,
          period: selectedPeriod,
          granularity: 'day', // Toujours récupérer les données quotidiennes, agrégation côté client
          customStartDate,
          customEndDate,
          compareEnabled,
          comparisonType
        })
      });

      const data = await response.json();

      if (data.success) {
        setGA4DashboardData(data);
        hasFetchedFreshDataRef.current = true; // Marquer que les données sont fraîches
      } else {
        setError(data.error || 'Erreur lors du chargement des données');
        setGA4DashboardData(null);
      }
    } catch (err) {
      // Requête remplacée par une plus récente : ne rien écraser
      if (err.name === 'AbortError') return;
      console.error('Erreur fetch dashboard GA4:', err);
      setError('Impossible de charger les données du dashboard');
      setGA4DashboardData(null);
    } finally {
      if (dashboardAbortRef.current === controller) {
        setGA4Loading(false);
      }
    }
  }, [token, ga4SelectedPropertyId, selectedPeriod, customStartDate, customEndDate, compareEnabled, comparisonType, setGA4Loading, setGA4DashboardData]);

  // Charger les données quand les paramètres changent (sauf granularité qui est gérée côté client)
  useEffect(() => {
    if (ga4SelectedPropertyId) {
      fetchDashboardData();
    }
  }, [ga4SelectedPropertyId, selectedPeriod, customStartDate, customEndDate, compareEnabled, comparisonType, fetchDashboardData]);

  // Fonction pour charger les données navigateurs
  const fetchBrowsersData = useCallback(async () => {
    if (!token || !ga4SelectedPropertyId || !ga4DashboardData?.currentPeriod?.startDate || !ga4DashboardData?.currentPeriod?.endDate) return;

    browsersAbortRef.current?.abort();
    const controller = new AbortController();
    browsersAbortRef.current = controller;

    setBrowsersLoading(true);
    try {
      const response = await fetch('/api/dashboard/ga4/browsers', {
        signal: controller.signal,
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          propertyId: ga4SelectedPropertyId,
          startDate: ga4DashboardData.currentPeriod.startDate,
          endDate: ga4DashboardData.currentPeriod.endDate
        })
      });

      const data = await response.json();
      if (data.success) {
        setBrowsersData(data.data);
      }
    } catch (err) {
      if (err.name === 'AbortError') return;
      console.error('Erreur fetch browsers GA4:', err);
    } finally {
      if (browsersAbortRef.current === controller) {
        setBrowsersLoading(false);
      }
    }
  }, [token, ga4SelectedPropertyId, ga4DashboardData?.currentPeriod?.startDate, ga4DashboardData?.currentPeriod?.endDate]);

  // Charger les navigateurs quand les données dashboard sont disponibles
  useEffect(() => {
    if (ga4DashboardData?.currentPeriod?.startDate && ga4DashboardData?.currentPeriod?.endDate) {
      fetchBrowsersData();
    }
  }, [ga4DashboardData?.currentPeriod?.startDate, ga4DashboardData?.currentPeriod?.endDate, fetchBrowsersData]);

  // Fonction pour charger les données langues
  const fetchLanguagesData = useCallback(async () => {
    if (!token || !ga4SelectedPropertyId || !ga4DashboardData?.currentPeriod?.startDate || !ga4DashboardData?.currentPeriod?.endDate) return;

    languagesAbortRef.current?.abort();
    const controller = new AbortController();
    languagesAbortRef.current = controller;

    setLanguagesLoading(true);
    try {
      const response = await fetch('/api/dashboard/ga4/languages', {
        signal: controller.signal,
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          propertyId: ga4SelectedPropertyId,
          startDate: ga4DashboardData.currentPeriod.startDate,
          endDate: ga4DashboardData.currentPeriod.endDate
        })
      });

      const data = await response.json();
      if (data.success) {
        setLanguagesData(data.data);
      }
    } catch (err) {
      if (err.name === 'AbortError') return;
      console.error('Erreur fetch languages GA4:', err);
    } finally {
      if (languagesAbortRef.current === controller) {
        setLanguagesLoading(false);
      }
    }
  }, [token, ga4SelectedPropertyId, ga4DashboardData?.currentPeriod?.startDate, ga4DashboardData?.currentPeriod?.endDate]);

  // Charger les langues quand les données dashboard sont disponibles
  useEffect(() => {
    if (ga4DashboardData?.currentPeriod?.startDate && ga4DashboardData?.currentPeriod?.endDate) {
      fetchLanguagesData();
    }
  }, [ga4DashboardData?.currentPeriod?.startDate, ga4DashboardData?.currentPeriod?.endDate, fetchLanguagesData]);

  // Totaux de la période, tels que GA4 les a calculés. Ils ne dépendent pas de la dimension
  // affichée : un tableau qui additionne ses lignes se trompe forcément, trop bas s'il n'en
  // reçoit qu'une partie, trop haut sur les métriques dédupliquées. Les deux conventions de
  // nommage des composants sont fournies.
  const buildPeriodTotals = useCallback((s) => {
    if (!s || !(s.totalVisits > 0)) return null;
    const users = s.uniqueVisitors ?? s.totalUsers ?? 0;
    const pageviews = s.totalPageviews ?? 0;
    return {
      sessions: s.totalVisits,
      activeUsers: users,
      users,
      pageviews,
      pageViews: pageviews,
      bounceRate: s.bounceRate ?? 0,
      avgSessionDuration: s.avgTimeOnSite ?? 0,
      conversions: s.conversions ?? 0,
      conversionsValue: s.conversionsValue ?? 0,
      transactions: s.transactions ?? 0,
      totalRevenue: s.totalRevenue ?? 0,
      conversionRate: s.conversionRate ?? 0,
      transactionRate: s.transactionRate ?? 0,
      // Le tableau des jours de la semaine utilise les clés françaises des métriques
      visites: s.totalVisits,
      visiteurs: users,
      pages_vues: pageviews,
      rebond: s.bounceRate ?? 0,
      pages_visite: s.totalVisits > 0 ? pageviews / s.totalVisits : 0,
      duree_moy: s.avgTimeOnSite ?? 0,
      taux_conv: s.conversionRate ?? 0,
      taux_transaction: s.transactionRate ?? 0,
      panier_moyen: (s.transactions ?? 0) > 0 ? (s.totalRevenue ?? 0) / s.transactions : 0
    };
  }, []);

  const periodTotals = useMemo(
    () => buildPeriodTotals(ga4DashboardData?.data?.summary),
    [buildPeriodTotals, ga4DashboardData?.data?.summary]
  );

  // La comparaison arrive sous deux formes selon que le mode comparaison est actif
  const previousPeriodTotals = useMemo(() => {
    const prev = ga4DashboardData?.previousPeriod;
    return buildPeriodTotals(prev?.summary || prev);
  }, [buildPeriodTotals, ga4DashboardData?.previousPeriod]);

  // Agréger les données quotidiennes selon la granularité sélectionnée (côté client)
  const aggregatedDailyData = useMemo(() => {
    const rawDaily = ga4DashboardData?.data?.daily || [];
    return aggregateByGranularity(rawDaily, ga4Granularity);
  }, [ga4DashboardData?.data?.daily, ga4Granularity]);

  // Handler pour le changement de propriété
  const handlePropertyChange = (e) => {
    const newPropertyId = e.target.value;
    if (newPropertyId) {
      setGA4SelectedPropertyId(newPropertyId);
      setGA4DashboardData(null);
      // Sans ça, ces deux panneaux affichent les chiffres de l'ancienne propriété
      // pendant tout le rechargement
      setBrowsersData(null);
      setLanguagesData(null);
    }
  };

  // Handler pour le changement de granularité
  const handleGranularityChange = (newGranularity) => {
    setGA4Granularity(newGranularity);
  };

  // Grouper les propriétés filtrées par compte
  const propertiesByAccount = filteredGA4Properties.reduce((acc, property) => {
    const accountKey = property.accountName || 'Compte par défaut';
    if (!acc[accountKey]) {
      acc[accountKey] = [];
    }
    acc[accountKey].push(property);
    return acc;
  }, {});

  // Cas 1: Chargement en cours (propriétés OU données OU attente auto-sélection)
  if (propertiesLoading || ga4Loading || waitingForData) {
    return (
      <DashboardLoadingScreen
        platformName="Google Analytics 4"
        features={[
          { icon: <Users size={28} />, label: 'Utilisateurs et sessions' },
          { icon: <TrendingUp size={28} />, label: 'Tendances et graphiques' },
          { icon: <img src="https://cdn.jsdelivr.net/gh/walkxcode/dashboard-icons/png/google-analytics.png" alt="Google Analytics" style={{width: '28px', height: '28px', borderRadius: '4px'}} />, label: 'Analyse du trafic' }
        ]}
      />
    );
  }

  // Cas 2: Aucune propriété GA4 configurée du tout
  if (ga4Properties.length === 0) {
    return (
      <div className="account-selection-prompt">
        <div className="account-selection-card">
          <div className="account-selection-icon">
            <img
              src="https://cdn.jsdelivr.net/gh/walkxcode/dashboard-icons/png/google-analytics.png"
              alt="Google Analytics 4"
              style={{ width: '48px', height: '48px', borderRadius: '8px' }}
            />
          </div>
          <h2>Aucune propriété Google Analytics configurée</h2>
          <p style={{ color: '#6b7280', marginBottom: '16px' }}>
            Connectez un compte Google Analytics 4 dans "Mes Connecteurs" pour commencer.
          </p>
          <button
            className="btn btn-primary"
            onClick={() => window.dispatchEvent(new CustomEvent('navigateTo', { detail: { view: 'connectors' } }))}
            style={{ fontSize: '16px', padding: '12px 24px' }}
          >
            + Ajouter un connecteur
          </button>
        </div>
      </div>
    );
  }

  // Cas 2b: Des propriétés existent mais aucune ne correspond au filtre client/site
  if (filteredGA4Properties.length === 0) {
    return (
      <div className="account-selection-prompt">
        <div className="account-selection-card">
          <div className="account-selection-icon">
            <span style={{ fontSize: '48px' }}>🔍</span>
          </div>
          <h2>Aucune propriété Google Analytics associée</h2>
          <p style={{ color: '#6b7280', marginBottom: '16px' }}>
            Aucune propriété Google Analytics 4 n'est associée au client ou site sélectionné.<br />
            Associez des comptes via "Gestion des sites" pour les voir apparaître ici.
          </p>
        </div>
      </div>
    );
  }

  // Cas 3: Propriétés disponibles mais aucune sélectionnée - Afficher le modal de sélection
  if (!ga4SelectedPropertyId || !filteredGA4Properties.find(p => p.id === ga4SelectedPropertyId)) {
    return (
      <div className="account-selection-prompt">
        <div className="account-selection-card">
          <div className="account-selection-icon">
            <img
              src="https://cdn.jsdelivr.net/gh/walkxcode/dashboard-icons/png/google-analytics.png"
              alt="Google Analytics 4"
              style={{ width: '48px', height: '48px', borderRadius: '8px' }}
            />
          </div>
          <h2>Sélectionnez une propriété Google Analytics</h2>
          <p style={{ color: '#6b7280', marginBottom: '16px' }}>
            {filteredGA4Properties.length} propriété{filteredGA4Properties.length > 1 ? 's' : ''} disponible{filteredGA4Properties.length > 1 ? 's' : ''}
          </p>
          <select
            className="account-selection-dropdown"
            value=""
            onChange={handlePropertyChange}
            style={{
              width: '100%',
              padding: '12px 16px',
              fontSize: '16px',
              borderRadius: '8px',
              border: '1px solid #d1d5db',
              backgroundColor: 'white',
              cursor: 'pointer'
            }}
          >
            <option value="">Choisir une propriété...</option>
            {Object.keys(propertiesByAccount).length > 1 ? (
              Object.entries(propertiesByAccount).map(([accountName, properties]) => (
                <optgroup key={accountName} label={accountName}>
                  {properties.sort((a, b) => a.name.localeCompare(b.name)).map(property => (
                    <option key={property.id} value={property.id}>
                      {property.name}
                    </option>
                  ))}
                </optgroup>
              ))
            ) : (
              [...filteredGA4Properties].sort((a, b) => a.name.localeCompare(b.name)).map(property => (
                <option key={property.id} value={property.id}>
                  {property.name}
                </option>
              ))
            )}
          </select>
        </div>
      </div>
    );
  }

  // Cas 4: Propriété sélectionnée - Afficher le dashboard (sans header)
  // Style pour les sections GA4
  const sectionStyle = {
    background: '#FAF9F5',
    borderRadius: '12px',
    padding: '24px',
    marginBottom: '24px'
  };

  // Vérifier si des données sont disponibles
  const summary = ga4DashboardData?.data?.summary;
  const hasData = summary && (
    (summary.totalVisits || 0) > 0 ||
    (summary.totalPageviews || 0) > 0 ||
    (summary.totalUsers || 0) > 0 ||
    (summary.sessions || 0) > 0 ||
    (summary.visits || 0) > 0 ||
    (summary.users || 0) > 0 ||
    (summary.uniqueVisitors || 0) > 0 ||
    (summary.pageviews || 0) > 0
  );

  // Si pas de données disponibles, afficher un message
  if (!hasData && ga4DashboardData && !ga4Loading) {
    return (
      <div className="ga4-dashboard-view">
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '60px 24px',
          backgroundColor: '#FAF9F5',
          borderRadius: '12px',
          textAlign: 'center'
        }}>
          <img
            src="https://cdn.jsdelivr.net/gh/walkxcode/dashboard-icons/png/google-analytics.png"
            alt="Google Analytics 4"
            style={{ width: '48px', height: '48px', borderRadius: '8px', opacity: 0.5, marginBottom: '16px' }}
          />
          <h3 style={{ color: '#64748b', fontWeight: '500', marginBottom: '8px' }}>
            Aucune donnée disponible pour cette période
          </h3>
          <p style={{ color: '#94a3b8', fontSize: '0.875rem' }}>
            Essayez de sélectionner une période différente ou vérifiez que le tracking Google Analytics est actif.
          </p>
        </div>
      </div>
    );
  }

  /**
   * Ce qu'on envoie à l'analyse : les données **déjà affichées**.
   *
   * Appelée à l'envoi de la question et non à l'ouverture du panneau : les blocs arrivent en
   * plusieurs temps, et une charge figée trop tôt capture un écran incomplet.
   */
  const construireChargeAnalyse = () => {
    const periode = {
      period: selectedPeriod,
      ...(selectedPeriod === 'CUSTOM' && { customStartDate, customEndDate })
    };

    const sourceDonnees = blocsAnalysables(ga4DashboardData);
    const blocs = {};
    const absents = [];
    // Le détail de cet écran vit sous des clés qui diffèrent d'une version à l'autre : on
    // parcourt ce qu'il y a, plutôt que d'en nommer une liste qui vieillirait mal.
    for (const [libelle, bloc] of Object.entries(sourceDonnees)) {
      if (!bloc || typeof bloc !== 'object') continue;
      const reduit = lignesDuBloc(bloc);
      if (reduit) { blocs[libelle] = reduit; continue; }
      // Un bloc sans lignes porte souvent les chiffres de tête — `currentPeriod` et consorts.
      const totaux = totauxDuBloc(bloc);
      if (totaux) blocs[libelle] = { totaux };
      else absents.push(libelle);
    }

    const site = (filteredGA4Properties || []).find((s) => String(s.id ?? s.idsite ?? s.idSite) === String(ga4SelectedPropertyId));

    return {
      dashboardData: {
        sources: [ga4SelectedPropertyId].filter(Boolean),
        ...periode,
        charts: sourceDonnees.charts,
        composants: { blocs, absents },
        dashboardType: 'ga4'
      },
      dashboardType: 'ga4',
      accountId: ga4SelectedPropertyId || null,
      accountName: site?.name || site?.displayName || null,
      ...periode
    };
  };

  return (
    <div className="ga4-dashboard-view" style={{ display: 'flex', flexDirection: 'column', gap: '0' }}>
      {/*
        L'analyse IA. Ce dashboard n'a pas de barre de filtres : le bouton occupe sa propre
        ligne, alignée à droite comme les autres actions d'écran. Le composant rend aussi le
        panneau, qui passe par un portail — sa place dans l'arbre n'a aucune incidence.
      */}
      {ga4SelectedPropertyId && !ga4Loading && ga4DashboardData && (
        <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '12px' }}>
          <AnalyseIA
            token={token}
            autorise={analyseAutorisee}
            compte={{ id: ga4SelectedPropertyId }}
            construireCharge={construireChargeAnalyse}
          />
        </div>
      )}

      {/* Barre avec lien Modules masqués */}
      {userRole !== 'CLIENT' && clientHiddenModules.length > 0 && (
        <div style={{
          display: 'flex',
          justifyContent: 'flex-end',
          marginBottom: '16px'
        }}>
          <div
            className="hidden-modules-link"
            onClick={() => setIsHiddenModulesModalOpen(true)}
            title="Voir les modules masqués pour le client"
          >
            <EyeOff size={16} />
            <span>Modules masqués</span>
            <span className="hidden-count">{clientHiddenModules.length}</span>
          </div>
        </div>
      )}

      {/* Modal des modules masqués */}
      <HiddenModulesModal
        isOpen={isHiddenModulesModalOpen}
        onClose={() => setIsHiddenModulesModalOpen(false)}
        hiddenModules={clientHiddenModules}
        onRestoreModule={(moduleId) => setClientModuleVisibility(moduleId, true)}
      />

      {/* Message d'erreur */}
      {error && (
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          padding: '12px 16px',
          backgroundColor: '#fef2f2',
          border: '1px solid #fecaca',
          borderRadius: '8px',
          marginBottom: '16px',
          color: '#b91c1c'
        }}>
          <AlertCircle size={16} />
          <span>{error}</span>
        </div>
      )}

      {/* Blocs manquants à cause d'une erreur, à ne pas confondre avec une absence de données */}
      {ga4DashboardData?.partial && (
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          padding: '12px 16px',
          backgroundColor: '#fffbeb',
          border: '1px solid #fde68a',
          borderRadius: '8px',
          marginBottom: '16px',
          color: '#92400e'
        }}>
          <AlertCircle size={16} />
          <span>
            Données incomplètes : {ga4DashboardData.failedBlocks?.join(', ')} n'ont pas pu être
            récupérées. Les panneaux concernés sont vides par erreur, pas par absence de trafic.
          </span>
        </div>
      )}

      {/* Graphique de performance GA4 */}
      {(userRole !== 'CLIENT' || isClientModuleVisible('ga4_performance')) && (
        <div style={sectionStyle}>
          <GA4PerformanceChart
            notes={dashboardNotes}
            data={aggregatedDailyData}
            summary={ga4DashboardData?.data?.summary}
            previousPeriodData={ga4DashboardData?.previousPeriod}
            granularity={ga4Granularity}
            onGranularityChange={handleGranularityChange}
            loading={ga4Loading}
            KPICard={KPICard}
            formatNumber={formatNumber}
            userRole={userRole}
            conversionConfig={ga4DashboardData?.conversionConfig}
            compareEnabled={compareEnabled}
            comparisonType={comparisonType}
            summaryVariations={ga4DashboardData?.summaryVariations}
            comparisonPeriod={ga4DashboardData?.comparisonPeriod}
            accountId={ga4SelectedPropertyId || 'global'}
            token={token}
            propertyId={ga4SelectedPropertyId}
            onConversionConfigSave={() => {
              // Recharger les données du dashboard pour appliquer la nouvelle config
              fetchDashboardData();
            }}
            isClientModuleVisible={isClientModuleVisible}
            toggleClientModuleVisibility={toggleClientModuleVisibility}
            moduleId="ga4_performance"
          />
        </div>
      )}

      {/* Analyse et Recommandations */}
      {(userRole !== 'CLIENT' || isClientModuleVisible('ga4_insights')) && (
        <div style={sectionStyle}>
          <GA4InsightsPanel
            summary={ga4DashboardData?.data?.summary}
            previousPeriodData={ga4DashboardData?.previousPeriod?.summary || ga4DashboardData?.previousPeriod}
            channels={ga4DashboardData?.data?.channels || []}
            geography={ga4DashboardData?.data?.geography || []}
            events={ga4DashboardData?.data?.events || []}
            formatNumber={formatNumber}
            userRole={userRole}
            isClientModuleVisible={isClientModuleVisible}
            toggleClientModuleVisibility={toggleClientModuleVisibility}
            moduleId="ga4_insights"
          />
        </div>
      )}

      {/* Composants GA4 séparés */}
      {(userRole !== 'CLIENT' || isClientModuleVisible('ga4_channels')) && (
        <div style={sectionStyle}>
          <GA4ChannelsChart
            channels={ga4DashboardData?.data?.channels || []}
            serverTotals={periodTotals}
            serverPreviousTotals={previousPeriodTotals}
            previousChannels={ga4DashboardData?.previousPeriod?.channels}
            compareEnabled={compareEnabled}
            formatNumber={formatNumber}
            loading={ga4Loading}
            summaryBounceRate={ga4DashboardData?.data?.summary?.bounceRate}
            conversionLabel={ga4DashboardData?.conversionConfig?.conversionLabel || 'Conversions'}
            accountId={ga4SelectedPropertyId || 'global'}
            userRole={userRole}
            isClientModuleVisible={isClientModuleVisible}
            toggleClientModuleVisibility={toggleClientModuleVisibility}
            moduleId="ga4_channels"
            token={token}
            propertyId={ga4SelectedPropertyId}
            conversionConfig={ga4DashboardData?.conversionConfig}
            onConversionConfigSave={() => fetchDashboardData()}
          />
        </div>
      )}

      {(userRole !== 'CLIENT' || isClientModuleVisible('ga4_geography')) && (
        <div style={sectionStyle}>
          <GA4GeographyChart
            geography={ga4DashboardData?.data?.geography || []}
            serverTotals={periodTotals}
            serverPreviousTotals={previousPeriodTotals}
            previousGeography={ga4DashboardData?.previousPeriod?.geography}
            compareEnabled={compareEnabled}
            formatNumber={formatNumber}
            loading={ga4Loading}
            summaryBounceRate={ga4DashboardData?.data?.summary?.bounceRate}
            conversionLabel={ga4DashboardData?.conversionConfig?.conversionLabel || 'Conversions'}
            accountId={ga4SelectedPropertyId || 'global'}
            userRole={userRole}
            isClientModuleVisible={isClientModuleVisible}
            toggleClientModuleVisibility={toggleClientModuleVisibility}
            moduleId="ga4_geography"
            token={token}
            propertyId={ga4SelectedPropertyId}
            conversionConfig={ga4DashboardData?.conversionConfig}
            onConversionConfigSave={() => fetchDashboardData()}
          />
        </div>
      )}

      {(userRole !== 'CLIENT' || isClientModuleVisible('ga4_devices')) && (
        <div style={sectionStyle}>
          <GA4DevicesChart
            devices={ga4DashboardData?.data?.devices?.devices || []}
            serverTotals={periodTotals}
            serverPreviousTotals={previousPeriodTotals}
            previousDevices={ga4DashboardData?.previousPeriod?.devices?.devices}
            compareEnabled={compareEnabled}
            formatNumber={formatNumber}
            loading={ga4Loading}
            conversionLabel={ga4DashboardData?.data?.devices?.conversionLabel || ga4DashboardData?.conversionConfig?.conversionLabel || 'Conversions'}
            summaryBounceRate={ga4DashboardData?.data?.summary?.bounceRate}
            accountId={ga4SelectedPropertyId || 'global'}
            userRole={userRole}
            isClientModuleVisible={isClientModuleVisible}
            toggleClientModuleVisibility={toggleClientModuleVisibility}
            moduleId="ga4_devices"
            token={token}
            propertyId={ga4SelectedPropertyId}
            conversionConfig={ga4DashboardData?.conversionConfig}
            onConversionConfigSave={() => fetchDashboardData()}
          />
        </div>
      )}

      {(userRole !== 'CLIENT' || isClientModuleVisible('ga4_browsers')) && (
        <div style={sectionStyle}>
          <GA4BrowsersChart
            browsers={browsersData?.browsers || []}
            serverTotals={periodTotals}
            serverPreviousTotals={previousPeriodTotals}
            previousBrowsers={[]}
            compareEnabled={false}
            formatNumber={formatNumber}
            loading={browsersLoading}
            conversionLabel={browsersData?.conversionLabel || ga4DashboardData?.conversionConfig?.conversionLabel || 'Conversions'}
            accountId={ga4SelectedPropertyId || 'global'}
            userRole={userRole}
            isClientModuleVisible={isClientModuleVisible}
            toggleClientModuleVisibility={toggleClientModuleVisibility}
            moduleId="ga4_browsers"
            token={token}
            propertyId={ga4SelectedPropertyId}
            conversionConfig={ga4DashboardData?.conversionConfig}
            onConversionConfigSave={() => fetchDashboardData()}
          />
        </div>
      )}

      {(userRole !== 'CLIENT' || isClientModuleVisible('ga4_languages')) && (
        <div style={sectionStyle}>
          <GA4LanguagesChart
            languages={languagesData?.languages || []}
            serverTotals={periodTotals}
            serverPreviousTotals={previousPeriodTotals}
            previousLanguages={[]}
            compareEnabled={false}
            formatNumber={formatNumber}
            loading={languagesLoading}
            conversionLabel={languagesData?.conversionLabel || ga4DashboardData?.conversionConfig?.conversionLabel || 'Conversions'}
            accountId={ga4SelectedPropertyId || 'global'}
            userRole={userRole}
            isClientModuleVisible={isClientModuleVisible}
            toggleClientModuleVisibility={toggleClientModuleVisibility}
            moduleId="ga4_languages"
            token={token}
            propertyId={ga4SelectedPropertyId}
            conversionConfig={ga4DashboardData?.conversionConfig}
            onConversionConfigSave={() => fetchDashboardData()}
          />
        </div>
      )}

      {ga4SelectedPropertyId && (userRole !== 'CLIENT' || isClientModuleVisible('ga4_weekdays')) && (
        <WeekdaysTable
          selectedAccount={{ id: ga4SelectedPropertyId }}
          selectedPeriod={selectedPeriod}
          customStartDate={customStartDate}
          customEndDate={customEndDate}
          formatNumber={formatNumber}
          isVisible={true}
          token={token}
          compareEnabled={compareEnabled}
          comparisonType={comparisonType}
          userRole={userRole}
          isClientModuleVisible={isClientModuleVisible}
          toggleClientModuleVisibility={toggleClientModuleVisibility}
          moduleId="ga4_weekdays"
          serverTotals={periodTotals}
          serverPreviousTotals={previousPeriodTotals}
          provider="ga4"
          propertyId={ga4SelectedPropertyId}
          conversionConfig={ga4DashboardData?.conversionConfig}
          onConversionConfigSave={() => fetchDashboardData()}
        />
      )}

      {(userRole !== 'CLIENT' || isClientModuleVisible('ga4_pages')) && (
        <div style={sectionStyle}>
          <GA4PagesChart
            pages={ga4DashboardData?.data?.pages?.pages || []}
            serverTotals={periodTotals}
            serverPreviousTotals={previousPeriodTotals}
            previousPages={ga4DashboardData?.previousPeriod?.pages?.pages}
            compareEnabled={compareEnabled}
            formatNumber={formatNumber}
            loading={ga4Loading}
            conversionLabel={ga4DashboardData?.data?.pages?.conversionLabel || ga4DashboardData?.conversionConfig?.conversionLabel || 'Conversions'}
            summaryBounceRate={ga4DashboardData?.data?.summary?.bounceRate}
            accountId={ga4SelectedPropertyId || 'global'}
            userRole={userRole}
            isClientModuleVisible={isClientModuleVisible}
            toggleClientModuleVisibility={toggleClientModuleVisibility}
            moduleId="ga4_pages"
            token={token}
            propertyId={ga4SelectedPropertyId}
            conversionConfig={ga4DashboardData?.conversionConfig}
            onConversionConfigSave={() => fetchDashboardData()}
          />
        </div>
      )}

      {(userRole !== 'CLIENT' || isClientModuleVisible('ga4_events')) && (
        <div style={sectionStyle}>
          <GA4EventsChart
            events={ga4DashboardData?.data?.events || []}
            previousEvents={ga4DashboardData?.previousPeriod?.events}
            compareEnabled={compareEnabled}
            formatNumber={formatNumber}
            loading={ga4Loading}
            totalSessions={ga4DashboardData?.data?.summary?.totalVisits || 0}
            previousTotalSessions={ga4DashboardData?.previousPeriod?.summary?.totalVisits || 0}
            userRole={userRole}
            isClientModuleVisible={isClientModuleVisible}
            toggleClientModuleVisibility={toggleClientModuleVisibility}
            moduleId="ga4_events"
          />
        </div>
      )}

      {/* Parcours d'achat e-commerce */}
      {blocsEcommerceVisibles && (userRole !== 'CLIENT' || isClientModuleVisible('ga4_ecommerce_funnel')) && (
        <div style={sectionStyle}>
          <GA4EcommerceFunnel
            token={token}
            propertyId={ga4SelectedPropertyId}
            period={selectedPeriod}
            customStartDate={customStartDate}
            customEndDate={customEndDate}
            formatNumber={formatNumber}
            userRole={userRole}
            isClientModuleVisible={isClientModuleVisible}
            toggleClientModuleVisibility={toggleClientModuleVisibility}
            moduleId="ga4_ecommerce_funnel"
          />
        </div>
      )}

      {/* Achats e-commerce par produit */}
      {blocsEcommerceVisibles && (userRole !== 'CLIENT' || isClientModuleVisible('ga4_ecommerce_products')) && (
        <div style={sectionStyle}>
          <GA4EcommerceProducts
            token={token}
            propertyId={ga4SelectedPropertyId}
            period={selectedPeriod}
            customStartDate={customStartDate}
            customEndDate={customEndDate}
            formatNumber={formatNumber}
            userRole={userRole}
            isClientModuleVisible={isClientModuleVisible}
            toggleClientModuleVisibility={toggleClientModuleVisibility}
            moduleId="ga4_ecommerce_products"
          />
        </div>
      )}

    </div>
  );
};

export default GA4DashboardView;
